import React, { useState } from 'react';
import { useSession } from '../../../../Components/Session';
import Modal from '../../../../Components/Modal';
import Form, { Actions, Address, PhoneNumber, PhoneNumberValidator, RequiredValidator, TextField } from '../../../../Components/Form';
import { Roles, useUser } from '../../../../Data/Users';
import { useBusiness } from '../../../../Data/Business';
import PendingVoucher from '../../../Billing/PendingVoucher';
import { useOnboardingJourney } from '../../../../Data/Onboarding';
import Recommend from './Recommend';
import { usePreference } from '../../../../Data/Preference';
import Icon from '../../../../Components/Icons/Icon';
import { classNames, useNewWindow } from '../../../../Services/helpers';
import { useDialogues } from '../../../../Components/Dialogues';
import { useNavigate } from 'react-router-dom';
import { useAssistantContext } from '../../../../Components/Assistant';
import Alert from '../../../../Components/Alert';
import Button from '../../../../Components/Button/Button';

const Onboarding = () => {
    const theJourney = useOnboardingJourney(true);
    const {user, business, refresh, isRoleSufficient} = useSession();
    const [hideGettingStarted, setHideGettingStarted] = usePreference('home:hideGettingStarted', false);

    if (user && !user.firstName) {
        return <UserNameRequired refresh={refresh} />
    }

    if (business && !business?.address?.postcode) {
        return <BusinessAddressRequiredRequired refresh={refresh} />
    }

    return <>
        {isRoleSufficient(Roles.Owner) && <PendingVoucher onComplete={refresh} />}

        {!hideGettingStarted && <GettingStarted setHideGettingStarted={setHideGettingStarted} />}

        <Recommend theJourney={theJourney} />
    </>
}

const GettingStarted = ({setHideGettingStarted}) => {
    const {setAssistantOpen} = useAssistantContext();
    const {confirm} = useDialogues();

    return <div className="surface-2 rounded-xl border p-3">
        <p className="mb-2 text-lg text-brand font-bold">
            Get started with getSoapy
            <Icon icon="XMark"
                className="float-right fill-slate-500 cursor-pointer"
                size="1.5x"
                onClick={() => confirm(
                    {
                        theme: 'primary',
                        title: 'Hide the help?',
                        message: 'These help options are always available in the More tab.',
                        confirmLabel: 'Hide',
                    },
                    () => setHideGettingStarted(true)
                )}
            />
        </p>

        {/* small - two rows */}
        <div className="grid grid-cols-2 grid-flow-row gap-2 md:hidden">
            <GettingStartedCard icon="Video"
                title="Videos"
                to="/help/videos"
                className="rounded-tl-lg"
            />
            <GettingStartedCard icon="ListCheck"
                title="Checklist"
                to="/help/checklist"
                className="rounded-tr-lg"
            />
        </div>
        <div className="mt-2 grid grid-cols-2 grid-flow-row gap-2 md:hidden">
            <GettingStartedCard icon="FileLines"
                title={<>Docs <Icon icon="LinkExternal" size="0.25x" className="ml-1" /></>}
                open="https://getsoapy.com/docs"
                className="rounded-bl-lg"
            />
            <GettingStartedCard icon="SidLight"
                iconTheme="soapy"
                title="Ask Sid"
                onClick={() => setAssistantOpen(true)}
                className="rounded-br-lg"
            />
        </div>

        {/* medium / large - single row */}

        <div className="hidden md:grid grid-cols-4 grid-flow-row gap-2">
            <GettingStartedCard icon="Video"
                title="Videos"
                to="/help/videos"
                className="rounded-l-lg"
            />
            <GettingStartedCard icon="ListCheck"
                title="Checklist"
                to="/help/checklist"
            />
            <GettingStartedCard icon="FileLines"
                title={<>Docs <Icon icon="LinkExternal" size="0.25x" className="ml-1" /></>}
                open="https://getsoapy.com/docs"
            />
            <GettingStartedCard icon="SidLight"
                iconTheme="soapy"
                title="Ask Sid"
                onClick={() => setAssistantOpen(true)}
                className="rounded-r-lg"
            />
        </div>
    </div>
}

const GettingStartedCard = ({icon, iconTheme, title, to, open, onClick, className}) => {
    const newWindow = useNewWindow();
    const navigate = useNavigate();

    const handleClick = () => {
        to && navigate(to);
        open && newWindow(open);
        onClick && onClick();
    }

    return <div className={classNames('p-2 rounded-sm bg-white border border-slate-300 text-center surface-1', className)}
            onClick={handleClick}
        >
        <Icon icon={icon} theme={iconTheme} className="fill-brand" size="2x" />
        <p className="mt-1 text-xs text-muted fill-muted">{title}</p>
    </div>
}


const UserNameRequired = (refresh) => {
    const {user, update} = useUser();

    return <Modal title="Welcome to getSoapy" isOpen={true} setIsOpen={() => null}>
        <Form data={user} onSubmit={update} afterSubmit={refresh} closeModalOnSubmit waitForData>
            <p className="text-muted">
                We're glad you're here! To get started, please tell us your name?
            </p>
            <TextField name="firstName" label="First name" validators={[RequiredValidator]} />
            <TextField name="lastName" label="Surname" validators={[RequiredValidator]} />
            <Actions submit={{label: 'Save'}} />
        </Form>
    </Modal>
}

const BusinessAddressRequiredRequired = (refresh) => {
    const {business, saveDetails} = useBusiness();
    const [isOpen, setIsOpen] = useState(true);

    if (!isOpen) {
        return <Alert
            icon="Info"
            title="Complete your business details"
            message="To enable some features including nearby leads and route optimisations, we need to know where you're based."
            buttons={[
                <Button theme="primary" label="Complete details" onClick={() => setIsOpen(true)} />
            ]}
        />
    }

    return <Modal title="Business details" isOpen={isOpen} setIsOpen={setIsOpen} dismissible>
        <Form data={business} onSubmit={saveDetails} afterSubmit={refresh} closeModalOnSubmit waitForData>
            <p className="text-muted">
                To enable some features including nearby leads and route optimisations, we need to know where you're based.
            </p>
            <TextField name="name" label="Business Name" validators={[RequiredValidator]} />
            <Address name="address" label="Address" validators={[RequiredValidator]} />
            <PhoneNumber name="phoneNumber" label="Phone number" validators={[PhoneNumberValidator]} />
            <Actions submit={{label: 'Save'}} cancel={{label: 'Later'}} />
        </Form>
    </Modal>
}



export default Onboarding;
